import React, { useEffect, useRef, useState } from "react";

//interfaces
import { IContentPreviewCarousel } from "../../../../interfaces/media/IContentPreviewCarousel";

//components
import ContentPreviewCarouselControls from "./ContentPreviewCarouselControls";
import ProgressiveLoadingImage from "../../images/ProgressiveLoadingImage";
import ContentPreviewTimeBelt from "./ContentPreviewTimeBelt";
import { AnimatePresence, motion } from "framer-motion";

//hooks
import { usePauseOtherMedia } from "../../../../hooks/usePauseOtherMedia";


const fadeVariants = {
    enter: { opacity: 0 },
    center: { opacity: 1 },
    exit: { opacity: 0 }
};

const ContentPreviewCarousel = ({className, mediaItems, token, videoAutoPlayEnabled}: IContentPreviewCarousel) => {  
    if (!Array.isArray(mediaItems) || mediaItems.length === 0) { return <></>}

    const [counter, setCounter] = useState<number>(0);
    const [isPaused, setIsPaused] = useState<boolean>(false);
    const activeItem = mediaItems[counter];
    const intervalRef = useRef<any>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const pauseOtherMedia = usePauseOtherMedia(videoRef);
    
    
    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!containerRef.current) return;
                if (entry.isIntersecting) {
                    setIsPaused(false);
                } else {
                    setIsPaused(true);
                }
            },
            {
                //this is how much of the video needs to be in the display before we play it - so like 100% is ideal
                threshold: 1
            }
        );

        observer.observe(containerRef.current);
        return () => {
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        if (!videoRef.current) return;

        if (!isPaused && videoAutoPlayEnabled) {
            pauseOtherMedia();

            videoRef.current.play()
            .catch(() => {
                console.error("Browser may have blocked autoplay")
            });
            
        } else {
            videoRef.current.pause();
        }
    }, [isPaused]);

    useEffect(() => {
        if (isPaused) return;

        startInterval();

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [isPaused, mediaItems.length]);

    function startInterval() {
        if (intervalRef.current) clearInterval(intervalRef.current);

        intervalRef.current = setInterval(() => {
            setCounter(p => (p + 1) % mediaItems.length);
        }, 6000);
    }

    function onPlayClick() {
        setIsPaused(!isPaused);
    }

    function onNavigateClick(direction: "previous" | "next") {
        setCounter(prev => {
            const next =
                direction === "previous"
                    ? (prev === 0 ? mediaItems.length - 1 : prev - 1)
                    : (prev === mediaItems.length - 1 ? 0 : prev + 1);

            return next;
        });

        startInterval();
    }

    return (
        <div ref={containerRef} className={`content-preview-carousel ${className ?? ""}`}>
            <div className={"carousel-frame"} >
                <AnimatePresence mode="wait">
                    <motion.div
                        key={counter}
                        className={`slide ${activeItem.mediaType === "video" ? "is-video" : "is-image"}`}
                        variants={fadeVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                        {(activeItem.mediaType === "image" || activeItem.mediaType === "audio") && activeItem.url &&
                            (<ProgressiveLoadingImage 
                                lowResUrl={activeItem.url} 
                                highResUrl={activeItem.url} 
                                token={token}
                            />)
                        }

                        {activeItem.mediaType === "video" && (<> 
                            <video
                                ref={videoRef}
                                autoPlay={!isPaused && videoAutoPlayEnabled}
                                muted
                                disablePictureInPicture 
                                aria-label={""}
                                src={`${token ? `${activeItem.url}?access_token=${token}` : activeItem.url}`}
                            />
                        </>)} 

                        <ContentPreviewTimeBelt uniqueId={counter} animationDuration={6} isPaused={isPaused} />
                    </motion.div>
                </AnimatePresence>
            </div>

            <ContentPreviewCarouselControls 
                currentItem={counter} 
                totalItems={mediaItems.length} 
                isPaused={isPaused} 
                onPlayButtonClick={onPlayClick} 
                onLeftClick={()=>onNavigateClick("previous")} 
                onRightClick={()=>onNavigateClick("next")}            
            />
        </div>
    );
};

export default ContentPreviewCarousel;
